import type { CSSProperties, ReactNode } from 'react';

/*
 * A small shared vocabulary rather than a CSS framework. Every interactive
 * element clears the 48px minimum tap target — this app gets used with gloves
 * on, one-handed, next to a running machine.
 */

export const TAP_MIN = 48;

export const card: CSSProperties = {
  padding: '15px 16px',
  borderRadius: 'var(--radius)',
  background: 'var(--surface)',
  border: '1px solid var(--line)',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

export const primaryButton: CSSProperties = {
  width: '100%',
  minHeight: 52,
  padding: '15px',
  borderRadius: 13,
  border: 'none',
  background: 'var(--lime)',
  color: '#11150f',
  font: '700 16px/1 var(--font-display)',
  cursor: 'pointer',
  transition: 'filter var(--motion), background var(--motion)',
};

export const ghostButton: CSSProperties = {
  flex: 1,
  minHeight: TAP_MIN,
  padding: '13px',
  borderRadius: 12,
  border: '1px solid var(--line-strong)',
  background: 'transparent',
  color: 'var(--text-3)',
  font: '600 13.5px/1 var(--font-body)',
  cursor: 'pointer',
  transition: 'border-color var(--motion), color var(--motion)',
};

export const disabledButton: CSSProperties = {
  background: 'rgba(255,255,255,.08)',
  color: 'var(--text-4)',
  cursor: 'not-allowed',
};

export function Eyebrow({ children, tone }: { children: ReactNode; tone?: string }) {
  return (
    <div
      style={{
        font: '600 11px/1 var(--font-body)',
        letterSpacing: '.14em',
        textTransform: 'uppercase',
        color: tone ?? 'var(--text-4)',
      }}
    >
      {children}
    </div>
  );
}

export function Title({ children }: { children: ReactNode }) {
  return (
    <h1 style={{ font: '700 22px/1.1 var(--font-display)', letterSpacing: '-.01em' }}>{children}</h1>
  );
}

export function Muted({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        font: '400 13px/1.45 var(--font-body)',
        color: 'var(--text-4)',
        textWrap: 'pretty',
      }}
    >
      {children}
    </div>
  );
}

/** Thin progress bar. `pct` is 0–100. */
export function Bar({ pct, tone = 'var(--lime)' }: { pct: number; tone?: string }) {
  return (
    <div
      style={{
        height: 6,
        borderRadius: 999,
        background: 'rgba(255,255,255,.08)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          height: '100%',
          width: `${Math.max(Math.min(pct, 100), 2)}%`,
          borderRadius: 999,
          background: tone,
          transition: 'width var(--motion)',
        }}
      />
    </div>
  );
}

/** Round percentage badge used on topic rows. */
export function Ring({ pct }: { pct: number }) {
  const active = pct > 0;
  return (
    <div
      className="num"
      style={{
        flex: 'none',
        width: 44,
        height: 44,
        borderRadius: 999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        font: '700 11.5px/1 var(--font-display)',
        color: active ? 'var(--lime)' : 'var(--text-4)',
        border: `2px solid ${active ? 'var(--lime-line)' : 'var(--line-strong)'}`,
        background: active ? 'oklch(0.82 0.17 130 / .08)' : 'transparent',
      }}
    >
      {pct}%
    </div>
  );
}

export function Screen({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        padding: '14px 20px 28px',
        display: 'flex',
        flexDirection: 'column',
        gap: 18,
      }}
    >
      {children}
    </div>
  );
}
